import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './Event.module.scss';
import { fetchData } from '@/services/fetchData';
import { BackendBookingDateType, EventType, SettingsType } from '@/types';
import { Layout } from '@/layout';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { SubEventsBlock } from '@/components/SubEventsBlock';
import { OneEventForm } from '@/components/OneEventForm';
import withAuth from '@/providers/WithAuth';
import { Gallery } from '@/components/Gallery';

const Event = ({
  menu,
  bookingDates,
  content,
  translations,
  locale,
  common,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData = content.item;
  const backendHTML = { __html: itemData.text };
  const showGallery =
    itemData.gallery.length > 0 || itemData.galleryMob.length > 0;
  const isSingleEvent = content.events.length === 1 && itemData.singleEvent;

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            {
              link: '/events',
              text: common.eventsLink,
            },
            {
              link: itemData.code,
              text: itemData.name,
            },
          ]}
        />
        {showGallery && (
          <Gallery
            galleryMob={itemData.galleryMob}
            gallery={itemData.gallery}
            title={itemData.name}
          />
        )}
        <div className="info" dangerouslySetInnerHTML={backendHTML} />
        {content.events.length > 0 && !isSingleEvent && (
          <SubEventsBlock
            translations={translations}
            events={content.events}
            code={itemData.code}
            dates={bookingDates.dates}
          />
        )}
        {isSingleEvent && (
          <OneEventForm
            events={content.events}
            bookingDates={bookingDates.dates}
            parentEvent={itemData.code}
            translations={translations}
            common={common}
            locale={locale}
          />
        )}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  params,
  locale,
}: GetServerSidePropsContext) {
  const paramsId = params?.event;

  const content = await fetchData({
    url: `/content/event/${paramsId}/view`,
    locale,
  });

  const bookingDates: { events: EventType[]; dates: BackendBookingDateType[] } =
    await fetchData({
      url: `/content/event/${paramsId}/dates`,
    });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;

  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      bookingDates,
      translations: menu.translations['/events/[event]'],
      locale,
      common: menu.translations.common,
    },
  };
}

export default withAuth(Event, true);
